import { SideDrawer } from '@theme/side-drawer';

/**
 * Custom Size Form Drawer
 * Extends SideDrawer, handles form submission and saves measurements
 * Measurements are stored in memory only (not persisted) and attached to cart line items
 */
class CustomSizeFormDrawer extends SideDrawer {
  /** @type {Record<string, string>} */
  static #measurements = {};

  connectedCallback() {
    super.connectedCallback();
    this.form = /** @type {HTMLFormElement | null} */ (this.querySelector('.custom-size-form'));
    this.form?.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.form?.removeEventListener('submit', this.#handleSubmit);
  }

  /**
   * @param {Event} event
   */
  #handleSubmit = (event) => {
    event.preventDefault();
    
    if (!this.form) return;
    
    const formData = new FormData(this.form);
    /** @type {Record<string, string>} */
    const measurements = {};
    
    for (const [key, value] of formData.entries()) {
      if (typeof value === 'string' && value.trim()) {
        measurements[key] = value.trim();
      }
    }
    
    // Save to static memory (not persisted)
    CustomSizeFormDrawer.#measurements = measurements;
    
    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('custom-measurements:updated', { 
      detail: { measurements } 
    }));
    
    // Show confirmation
    this.#showConfirmation();
    
    // Close drawer and reset form after short delay
    setTimeout(() => {
      this.close();
      this.form?.reset();
    }, 1500);
  };

  #showConfirmation() {
    const button = /** @type {HTMLButtonElement | null} */ (
      this.form?.querySelector('.custom-size-form__save')
    );
    if (button) {
      const originalText = button.textContent;
      button.textContent = 'Saved ✓';
      button.disabled = true;
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1500);
    }
  }

  /**
   * Get current measurements for adding to cart
   * @returns {Record<string, string>} measurements object
   */
  static getMeasurements() {
    return { ...CustomSizeFormDrawer.#measurements };
  }

  /**
   * Clear stored measurements (called after adding to cart)
   */
  static clearMeasurements() {
    CustomSizeFormDrawer.#measurements = {};
  }

  /**
   * Get measurements as line item properties format
   * Prefixes each measurement with "Custom: " for clarity in order
   * @returns {Record<string, string>} properties for cart line item
   */
  static getLineItemProperties() {
    const measurements = CustomSizeFormDrawer.getMeasurements();
    /** @type {Record<string, string>} */
    const properties = {};
    
    Object.entries(measurements).forEach(([key, value]) => {
      properties[`Custom ${key}`] = `${value}"`;
    });
    
    return properties;
  }
}

if (!customElements.get('custom-size-form-drawer')) {
  customElements.define('custom-size-form-drawer', CustomSizeFormDrawer);
}

export { CustomSizeFormDrawer };
